import { APMultiSelect } from '@/components/ap/form';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { AppConfig } from '@/config/appConfig';
import { filterLanguageOptionsForMode, LANGUAGE_SELECT_OPTIONS } from '@/i18n/supportedLanguages';
import {
  DEFAULT_FONT_SCALE,
  FONT_SCALE_STEPS,
  FontFamilyPref,
  getAvailableThemeModes,
  LetterSpacing,
  LineHeight,
  selectFontFamily,
  selectFontScale,
  selectLetterSpacing,
  selectLineHeight,
  selectSetFontFamily,
  selectSetFontScale,
  selectSetLetterSpacing,
  selectSetLineHeight,
  selectSetThemeMode,
  selectThemeMode,
  ThemeMode,
  useThemeStore,
} from '@/lib/store/themeStore';
import {
  BoltLinear,
  LeafLinear,
  MonitorLinear,
  MoonLinear,
  PaletteLinear,
  SunLinear,
} from '@assessprep/icons/solar';
import { ComponentType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * APL-11850 (accessibility-modal rework) — the former sidebar "Display"
 * popover as a kit Dialog, opened from the avatar menu and from the take /
 * preview tools rail (`hideLanguage` there — mid-exam language switching is
 * out). Same live-applying themeStore wiring as the popover era: theme,
 * font-size %, line-height, letter-spacing — plus the reading-font section (Lexie
 * Readable / Open-Dyslexic / SF Pro Display, v3 "Dyslexia Font" files
 * reused). Theme list is env-filtered (`getAvailableThemeModes`): Pastel /
 * Funky are dev/staging-only. The Direction row is gone — direction derives
 * from the locale (ar_AR → rtl) via useEffectiveDirection.
 */

const MODE_OPTIONS: { value: ThemeMode; labelKey: string; Icon: ComponentType<any> }[] = [
  { value: 'light', labelKey: 'V4Shell.theme_light', Icon: SunLinear },
  { value: 'dark', labelKey: 'V4Shell.theme_dark', Icon: MoonLinear },
  { value: 'high-contrast', labelKey: 'V4Shell.theme_high_contrast', Icon: PaletteLinear },
  { value: 'pastel', labelKey: 'V4Shell.theme_pastel', Icon: LeafLinear },
  { value: 'funky', labelKey: 'V4Shell.theme_funky', Icon: BoltLinear },
  { value: 'system', labelKey: 'V4Shell.theme_system', Icon: MonitorLinear },
];

const FONT_OPTIONS: { value: FontFamilyPref; labelKey: string }[] = [
  { value: 'default', labelKey: 'V4Shell.font_default' },
  { value: 'lexie-readable', labelKey: 'V4Shell.font_lexie_readable' },
  { value: 'open-dyslexic', labelKey: 'V4Shell.font_open_dyslexic' },
  { value: 'sf-pro-display', labelKey: 'V4Shell.font_sf_pro_display' },
];

const FieldLabel = ({ children }: { children: ReactNode }) => (
  <span className="text-xs font-medium text-muted-foreground">{children}</span>
);

/** Injected, not imported: `components/ap` may not import from `pages/**` (three-layer law). */
export interface ExperienceScopedReadingPrefs {
  theme?: ThemeMode;
  /** An absent field inherits the device pref, so entering an exam never resets it. */
  fontScale?: number;
  letterSpacing?: LetterSpacing;
  lineHeight?: LineHeight;
  fontFamily?: FontFamilyPref;
  /** Partial — only the changed field. `fontScale: null` CLEARS the choice (Reset). */
  onChange: (patch: {
    theme?: ThemeMode;
    fontScale?: number | null;
    letterSpacing?: LetterSpacing;
    lineHeight?: LineHeight;
    fontFamily?: FontFamilyPref;
  }) => void;
}

export interface DisplaySettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** In-test surfaces hide the language dropdown (mid-exam locale switches
   *  would fight the exam's language accommodation flow). */
  hideLanguage?: boolean;
  experiencePrefs?: ExperienceScopedReadingPrefs;
}

export const DisplaySettingsDialog = ({
  open,
  onOpenChange,
  hideLanguage = false,
  experiencePrefs,
}: DisplaySettingsDialogProps) => {
  const { t, i18n } = useTranslation();
  const deviceMode = useThemeStore(selectThemeMode);
  const setDeviceMode = useThemeStore(selectSetThemeMode);
  const deviceFontScale = useThemeStore(selectFontScale);
  const setDeviceFontScale = useThemeStore(selectSetFontScale);
  const deviceLetterSpacing = useThemeStore(selectLetterSpacing);
  const setDeviceLetterSpacing = useThemeStore(selectSetLetterSpacing);
  const deviceLineHeight = useThemeStore(selectLineHeight);
  const setDeviceLineHeight = useThemeStore(selectSetLineHeight);
  const deviceFontFamily = useThemeStore(selectFontFamily);
  const setDeviceFontFamily = useThemeStore(selectSetFontFamily);

  const mode = experiencePrefs?.theme ?? deviceMode;
  const setMode = (value: ThemeMode) =>
    experiencePrefs ? experiencePrefs.onChange({ theme: value }) : setDeviceMode(value);
  const fontScale = experiencePrefs?.fontScale ?? deviceFontScale;
  const letterSpacing = experiencePrefs?.letterSpacing ?? deviceLetterSpacing;
  const lineHeight = experiencePrefs?.lineHeight ?? deviceLineHeight;
  const fontFamily = experiencePrefs?.fontFamily ?? deviceFontFamily;

  const setFontScale = (value: number) =>
    experiencePrefs ? experiencePrefs.onChange({ fontScale: value }) : setDeviceFontScale(value);
  const setLetterSpacing = (value: LetterSpacing) =>
    experiencePrefs
      ? experiencePrefs.onChange({ letterSpacing: value })
      : setDeviceLetterSpacing(value);
  const setLineHeight = (value: LineHeight) =>
    experiencePrefs ? experiencePrefs.onChange({ lineHeight: value }) : setDeviceLineHeight(value);
  const setFontFamily = (value: FontFamilyPref) =>
    experiencePrefs ? experiencePrefs.onChange({ fontFamily: value }) : setDeviceFontFamily(value);

  const availableModes = getAvailableThemeModes();
  const modeOptions = MODE_OPTIONS.filter((option) => availableModes.includes(option.value));

  const currentLocale = i18n.language;
  const languageOptions = filterLanguageOptionsForMode(LANGUAGE_SELECT_OPTIONS, AppConfig.mode).map(
    (option) => ({ value: option.value, label: option.label })
  );

  const scaleIndex = (FONT_SCALE_STEPS as readonly number[]).indexOf(fontScale);
  const stepFontScale = (direction: -1 | 1) => {
    const next = FONT_SCALE_STEPS[scaleIndex + direction];
    if (next !== undefined) setFontScale(next);
  };

  // Reset clears the choice (null), never writes 100: an explicit 100 would beat a non-100 Display pref.
  const fontScaleResettable = experiencePrefs
    ? experiencePrefs.fontScale !== undefined
    : fontScale !== DEFAULT_FONT_SCALE;
  const resetFontScale = () =>
    experiencePrefs
      ? experiencePrefs.onChange({ fontScale: null })
      : setDeviceFontScale(DEFAULT_FONT_SCALE);

  const SPACING_OPTIONS: { value: LetterSpacing; labelKey: string }[] = [
    { value: 'normal', labelKey: 'V4Shell.letter_spacing_normal' },
    { value: 'wide', labelKey: 'V4Shell.letter_spacing_wide' },
    { value: 'wider', labelKey: 'V4Shell.letter_spacing_wider' },
  ];

  const LINE_HEIGHT_OPTIONS: { value: LineHeight; labelKey: string }[] = [
    { value: 'normal', labelKey: 'V4Shell.line_height_normal' },
    { value: 'relaxed', labelKey: 'V4Shell.line_height_relaxed' },
    { value: 'loose', labelKey: 'V4Shell.line_height_loose' },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* max-h + scroll: at 150% root scale the modal outgrows short viewports —
          its own controls must stay reachable (FeedbackDialog pattern). */}
      <DialogContent
        className="max-h-[85vh] overflow-y-auto sm:max-w-md"
        data-testid="display-settings-modal"
      >
        <DialogHeader>
          <DialogTitle>{t('V4Shell.accessibility')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <FieldLabel>{t('V4Shell.theme')}</FieldLabel>
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              spacing={1}
              value={mode}
              // Radix emits '' when the active item is re-clicked — keep the choice.
              onValueChange={(value) => value && setMode(value as ThemeMode)}
              aria-label={t('V4Shell.theme')}
              className="grid w-full grid-cols-2"
            >
              {modeOptions.map(({ value, labelKey, Icon }) => (
                <ToggleGroupItem
                  key={value}
                  value={value}
                  // Selected ≠ hover: primary border + primary wash (kit-wide
                  // selected-card idiom). Forced-colors keeps the Highlight fill.
                  className="gap-1.5 px-2 data-[state=on]:border-primary data-[state=on]:bg-ap-primary-a10 forced-colors:data-[state=on]:bg-accent"
                  data-testid={`v4-theme-option-${value}`}
                >
                  <Icon aria-hidden className="size-4" />
                  <span className="truncate">{t(labelKey)}</span>
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>

          {!hideLanguage && (
            <div className="flex flex-col gap-1.5">
              <FieldLabel>{t('CommonText.language')}</FieldLabel>
              <APMultiSelect
                options={languageOptions}
                multiple={false}
                showBadges={false}
                value={[currentLocale]}
                onChange={(values) => {
                  // Re-picking the active language clears the selection (v3
                  // allowClear parity on APMultiSelect) — ignore, `value` stays
                  // pinned to the current locale via the controlled prop above.
                  if (values[0]) i18n.changeLanguage(String(values[0]));
                }}
                placeholder={t('CommonText.language')}
                className="w-full"
                data-testid="v4-language-select"
              />
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <FieldLabel>{t('V4Shell.font_family')}</FieldLabel>
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              spacing={1}
              value={fontFamily}
              onValueChange={(value) => value && setFontFamily(value as FontFamilyPref)}
              aria-label={t('V4Shell.font_family')}
              className="grid w-full grid-cols-2"
            >
              {FONT_OPTIONS.map(({ value, labelKey }) => (
                <ToggleGroupItem
                  key={value}
                  value={value}
                  className="px-2"
                  data-testid={`display-font-option-${value}`}
                >
                  <span className="truncate">{t(labelKey)}</span>
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex h-6 items-center justify-between">
              <FieldLabel>{t('V4Shell.font_size')}</FieldLabel>
              {fontScaleResettable && (
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  className="text-muted-foreground"
                  onClick={resetFontScale}
                  data-testid="v4-font-scale-reset"
                >
                  {t('CommonText.reset')}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                aria-label={t('V4Shell.font_size_decrease')}
                disabled={scaleIndex <= 0}
                onClick={() => stepFontScale(-1)}
                data-testid="v4-font-scale-down"
              >
                <span aria-hidden className="text-xs">
                  A−
                </span>
              </Button>
              <span
                aria-live="polite"
                className="flex-1 text-center text-sm tabular-nums"
                data-testid="v4-font-scale-value"
              >
                {fontScale}%
              </span>
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                aria-label={t('V4Shell.font_size_increase')}
                disabled={scaleIndex >= FONT_SCALE_STEPS.length - 1}
                onClick={() => stepFontScale(1)}
                data-testid="v4-font-scale-up"
              >
                <span aria-hidden className="text-base">
                  A+
                </span>
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <FieldLabel>{t('V4Shell.line_height')}</FieldLabel>
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              spacing={1}
              value={lineHeight}
              onValueChange={(value) => value && setLineHeight(value as LineHeight)}
              aria-label={t('V4Shell.line_height')}
              className="grid w-full grid-cols-3"
            >
              {LINE_HEIGHT_OPTIONS.map(({ value, labelKey }) => (
                <ToggleGroupItem
                  key={value}
                  value={value}
                  data-testid={`display-line-height-option-${value}`}
                >
                  <span className="truncate">{t(labelKey)}</span>
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>

          <div className="flex flex-col gap-1.5">
            <FieldLabel>{t('V4Shell.letter_spacing')}</FieldLabel>
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              spacing={1}
              value={letterSpacing}
              onValueChange={(value) => value && setLetterSpacing(value as LetterSpacing)}
              aria-label={t('V4Shell.letter_spacing')}
              className="grid w-full grid-cols-3"
            >
              {SPACING_OPTIONS.map(({ value, labelKey }) => (
                <ToggleGroupItem
                  key={value}
                  value={value}
                  data-testid={`v4-letter-spacing-${value}`}
                >
                  <span className="truncate">{t(labelKey)}</span>
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
